import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getUser, updateUser, getAllRoles } from "../../api/Users";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
/* import EditForm from "../../components/EditForm";
 */
import UserForm from "../../components/user/UserForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import { getAccountTypesByAgreement } from "../../api/Agreement";

const EditUserPage = () => {
  const { userPublicId, agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (userPublicId) {
      handleGetUser();
      handleGetBillingGroups();
      handleGetAccountTypes();
      handleGetRoles();
    }
  }, [userPublicId]);

  const handleGetUser = async () => {
    try {
      const accessToken = await getAccessToken();
      const newUser = await getUser(userPublicId, accessToken);

      if (billingGroups.length === 0) {
        await handleGetBillingGroups();
        await handleGetAccountTypes();
        await handleGetRoles();
      }

      if (newUser.billingGroupId) {
        const matchedGroup = billingGroups.find(
          (group) => group.billingGroupId === newUser.billingGroupId
        );

        if (matchedGroup) {
          newUser.billingGroupName = matchedGroup.name;
        }
      }

      setUser(newUser);
    } catch (error) {
      console.error("Failed to fetch user:", error);
    }
    setLoadingUser(false);
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByAgreement(agreementPublicId, accessToken);
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleGetAccountTypes = async () => {
    try {
      const accessToken = await getAccessToken();
      const at = await getAccountTypesByAgreement(
        agreementPublicId,
        accessToken
      );
      setAccountTypes(at);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleGetRoles = async () => {
    try {
      const accessToken = await getAccessToken();
      const role = await getAllRoles(
        accessToken
      );

      setRoles(role);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState({
        isOpen: true,
        title: "Discard changes?",
        confirm: () => navigate(-1),
      });
    } else {
      navigate(-1);
    }
  };

  const handleBillingGroupChange = (option) => {
    const selectedGroup = billingGroups.find((group) => group.name === option);
    if (!selectedGroup) return;
    setUser((prevUser) => ({
      ...prevUser,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    setHasChanges(true);
  };

  const handleAccountTypeChange = (option) => {
    const selectedAccountType = accountTypes.find((group) => group.name === option);
    if (!selectedAccountType) return;
    setUser((prevUser) => ({
      ...prevUser,
      accountTypeName: selectedAccountType.name,
      accountTypepPublicId: selectedAccountType.accountTypepPublicId,
    }));
    setHasChanges(true);
  };

  const handleRoleChange = (option) => {
    const selectedRole = roles.find((group) => group.name === option);
    if (!selectedRole) return;
    setUser((prevUser) => ({
      ...prevUser,
      roleName: selectedRole.roleName,
    }));
    setHasChanges(true);
  };

  const handleSaveUser = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      const updatedUser = {
        userPrincipalName: user.userPrincipalName,
        displayName: user.displayName,
        officeLocation: user.officeLocation,
        mobilePhone: user.mobilePhone,
        country: user.country,
        billingGroupId: user.billingGroupId,
        accountTypeId: user.accountTypeId,
        roles: user.roles,
      };

      console.log("Sending updated user:", updatedUser); // Debug: Check what is being sent

      await updateUser(user.userPublicId, updatedUser, accessToken);
      toast.success("User updated successfully", {
        iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" },
      });
      window.dispatchEvent(new Event("userUpdated"));

      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to update user");
    }
    navigate(-1);
  };

  console.log("USER:", user);
  console.log("AT:", accountTypes)
  console.log("ROLES:", roles)

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Edit User"} loading={loadingUser} />
          </div>
          <div className={`${loadingUser ? "loading" : ""} page-content`}>
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Edit user: {user?.displayName || "Unknown"}</h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleSaveUser}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <UserForm
                user={user}
                setUser={setUser}
                setHasChanges={setHasChanges}
                disabled={false}
                accountTypes={accountTypes}
                billingGroups={billingGroups}
                roles={roles}
                onAccountTypeChange={handleAccountTypeChange}
                onBillingGroupChange={handleBillingGroupChange}
                onRoleChange={handleRoleChange}                
              />


            </div>
          </div>
        </main>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title || "Discard changes?"}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={() => {
          setConfirmState({ isOpen: false });
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default EditUserPage;
